import { AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';
import type { Experiment } from '../api.js';
import { formatNumber } from '../lib/format.js';

/**
 * §107.3 — "BU DENEY SİTEDE UYGULANIYOR MU?"
 *
 * NEDEN VAR: SDK, site `dxp.variant()` çağırsın ya da ÇAĞIRMASIN atama damgasını
 * her olaya basar (§63.1/4). Yani deneyi panelde açıp sitesine hiç dokunmayan bir
 * kullanıcının deneyi de oturum toplar, `experiment_results` dolar ve mSPRT
 * "kazanan" ilan EDEBİLİR — oysa iki kol da birebir aynı sayfayı görmüştür.
 * Faz 6'nın açılış değişmezi bunun tam tersini söyler: "yanlış 'kazanan' ilanı
 * üründen kötüdür" (§61.2). Bu bileşen o boşluğu kapatır.
 *
 * NE KANITLAR, NE KANITLAMAZ: sunucudan `variant()` çağrısı GÖRÜLEMEZ. Görülebilen
 * şey, atanmış oturumun deneyin koştuğunu SÖYLEDİĞİ sayfayı görüp görmediğidir.
 * Bu güçlü bir göstergedir, kanıt değildir — metin de bunu "olabilir" diliyle
 * söyler. Kesin kanıt SDK'dan yeni bir sinyal isterdi (30KB gzip bütçesi + §67'nin
 * "varyantı ürün çizmez" çizgisi).
 *
 * DÖRT HÂL AYRIDIR (§107.3 tablosu). Özellikle `null` (henüz sayılmadı) ile `0`
 * (hiçbiri görmedi) KARIŞTIRILMAZ: ilkini uyarıya çevirmek, "bilmiyorum"u "kesin
 * yanlış" diye okumak olurdu.
 */

type Ton = 'uyari' | 'iyi' | 'notr';

interface Durum {
  ton: Ton;
  metin: string;
}

export function uygulamaDurumu(experiment: Experiment, atanmisOturum: number): Durum {
  /*
   * §109.4 — YÜZEY İKİ BİÇİMDE OLABİLİR: inline deneyde `targetPattern`, split-URL
   * deneyinde VARYANT URL'LERİ (orada `targetPattern` tanım gereği NULL'dır).
   *
   * Bu ayrım ilk yazımda atlanmıştı: split deney, motor sayımı yapmış olmasına
   * rağmen panelde "yüzey tanımlı değil — doğrulanamıyor" diyordu. Yani doğru
   * çalışan bir deney, ürünün kendi uyarısıyla şüpheli gösteriliyordu.
   */
  const varyantUrlleri = experiment.variants
    .map((v) => v.url)
    .filter((u): u is string => typeof u === 'string' && u !== '');
  const yuzey = experiment.targetPattern ?? (varyantUrlleri.length > 0 ? varyantUrlleri.join(' / ') : null);

  if (yuzey === null) {
    return {
      ton: 'notr',
      metin:
        'Test yüzeyi tanımlı değil — bu deneyin sitede uygulanıp uygulanmadığı DOĞRULANAMIYOR.',
    };
  }
  if (experiment.targetSeenSessions === null) {
    return { ton: 'notr', metin: 'Henüz değerlendirilmedi — uygulama durumu ilk turdan sonra belli olur.' };
  }
  if (experiment.targetSeenSessions === 0 && atanmisOturum > 0) {
    return {
      ton: 'uyari',
      metin:
        `Bu deneye atanan ${formatNumber(atanmisOturum)} oturumun HİÇBİRİ hedef sayfayı ` +
        `(${yuzey}) görmedi — deney sitede uygulanmamış olabilir. ` +
        'Sonuçlara bu ihtimali göz önünde tutarak bakın.',
    };
  }
  return {
    ton: 'iyi',
    metin: `${formatNumber(experiment.targetSeenSessions)} atanmış oturum hedef sayfayı (${yuzey}) gördü.`,
  };
}

const STIL: Record<Ton, { kutu: string; ikon: typeof AlertTriangle }> = {
  uyari: { kutu: 'border-[#fde68a] bg-[#fffbeb] text-[#92400e]', ikon: AlertTriangle },
  iyi: { kutu: 'border-[#e5e7eb] bg-[#f9fafb] text-[#374151]', ikon: CheckCircle2 },
  notr: { kutu: 'border-[#e5e7eb] bg-[#f9fafb] text-[#6b7280]', ikon: HelpCircle },
};

export default function DeneyUygulamaDurumu({
  experiment,
  atanmisOturum,
}: {
  experiment: Experiment;
  /** Deneye atanmış toplam oturum (kol sayılarının toplamı). */
  atanmisOturum: number;
}) {
  const durum = uygulamaDurumu(experiment, atanmisOturum);
  const stil = STIL[durum.ton];
  const Ikon = stil.ikon;

  return (
    <p
      data-testid="deney-uygulama-durumu"
      data-ton={durum.ton}
      className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-[12px] ${stil.kutu}`}
    >
      <Ikon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{durum.metin}</span>
    </p>
  );
}
